import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { activateCandidateBySuperAdmin, centreInactiveThunk, createAdminUsersThunk, createCentreUserThunk, createNewCentreThunk, declineEnquiryThunk, DeleteCandidateCommentsThunk, getAdminUserDetailThunk, getAdminUsersThunk, getAllActiveCentresListThunk, getAllActiveCentresThunk, getAllCandidatesThunk, getAllPendingEnquiriesThunk, getCandidateComments, getCandidateData, getCentreDetailsThunk, getCentresUsersThunk, getEnquiryApproveThunk, getEnquiryDetailsThunk, getReferooDocumentThunk, otherRequestsThunk, postCandidateCommentsThunk, reopenStageThunk, setUpApprovedCentresThunk, submitJobAdderIdThunk, updateAdminUserDetailThunk, updateCentreDetailsThunk, updateCentreUserThunk, updateStepBypassReasonThunk, uploadsCentrePictureThunk } from './thunks';
import {
    ICandidateData,
    ICandidateDetails,
    ICenterDetails,
    IPaginatedData,
    IPendingEnquiry,
    ISuperAdminState,
    TCenterDetails,
    TEnquiryDetails,
    TJobAdderPopupDetails
} from './types';

const initialState: ISuperAdminState = {
    initial: null,
    inProgress: false,
    saveBypassInProgress: false,
    declineInProgress: false,
    listingInProgress: false,
    error: '',
    super_admin_user: null,
    general_admin_user: null,
    centre_admins: [],
    allCenterList: null,
    centre_users: [],
    allCandidates: null,
    allPendingEnquiriesList: null,
    enquiryDetails: null,
    allActiveCentresList: null,
    allActiveCentres: null,
    centreDetails: null,
    reviewCandidateData: null,
    reopenedStagesStatus: null,
    candidateAdminComments: null,
    centrePicImage: '',
    adminUserList: null,
    totalActiveCentresCount: 0,
    totalPendingCentresCount: 0,
    totalCentreAdminCount: 0,
    totalCentreUserCount: 0,
    adminCommentsPostingProgress: false,
    makeInactiveInProgress: false,
    showJobAdderPopup: null,
    referooDocumentsFound: false,
    referooRightToWorkDoc: [],
    referooReferenceCheckDoc: [],
    rightToWorkDoc: [],
    policeCheckDoc: [],
    emergencyinfodetails: null,
    reopenStageError: null,
}

const superAdminSlice = createSlice({
    name: 'superadmin',
    initialState,
    reducers: {
        resetSuperAdminErrorAction: (state) => {
            state.error = null
        },
        setAllCandidateListAction: (state, action: PayloadAction<ICandidateData>) => {
            state.allCandidates = action.payload
        },
        setAllPendingEnquiriesAction: (state, action: PayloadAction<IPaginatedData<IPendingEnquiry>>) => {
            state.allPendingEnquiriesList = action.payload
        },
        clearAllPendingEnquiriesAction: (state) => {
            state.allPendingEnquiriesList = null
        },
        setEnquiryDetailsAction: (state, action: PayloadAction<TEnquiryDetails>) => {
            state.enquiryDetails = action.payload
        },
        setAllActiveCentresListAction: (state, action: PayloadAction<IPaginatedData<TCenterDetails>>) => {
            state.allActiveCentresList = action.payload
        },
        setAllActiveCentresAction: (state, action: PayloadAction<TCenterDetails[]>) => {
            state.allActiveCentres = action.payload
        },
        setCentreDetailsAction: (state, action: PayloadAction<ICenterDetails>) => {
            state.centreDetails = action.payload
        },
        setCandidateReviewDataAction: (state, action: PayloadAction<ICandidateDetails>) => {
            state.reviewCandidateData = action.payload
            state.reopenedStagesStatus = action.payload.reopened_stages_status || null
        },        
        setCandidateAdminComments: (state, action) => {
            state.candidateAdminComments = action.payload
        },
        setCentrePicAction: (state, action) => {
            state.centrePicImage = action.payload
        },
        setAdminUserListAction: (state, action) => {
            state.adminUserList = action.payload
        },
        setTotalActiveCentresCountAction: (state, action) => {
            state.totalActiveCentresCount = action.payload
        },
        setTotalPendingCentresCountAction: (state, action) => {
            state.totalPendingCentresCount = action.payload
        },
        setTotalCentreAdminCountAction: (state, action) => {
            state.totalCentreAdminCount = action.payload
        },
        setTotalCentreUserCountAction: (state, action) => {
            state.totalCentreUserCount = action.payload
        },
        resetCentrePicAction: (state) => {
            state.centrePicImage = ''
        },
        setJobAdderIdPopupShowAction: (state, action: PayloadAction<TJobAdderPopupDetails>) => {
            state.showJobAdderPopup = action.payload
        },
        resetReferooDocumentsAction: (state) => {
            state.referooDocumentsFound = false;
            state.referooRightToWorkDoc = [];
            state.referooReferenceCheckDoc = [];
            state.rightToWorkDoc = [];
            state.policeCheckDoc = [];
        },
    },
    extraReducers: (builder) => {
        // submit the form to create the admin users
        builder.addCase(createAdminUsersThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(createAdminUsersThunk.fulfilled, (state, action) => {
            state.inProgress = false
            sessionStorage.clear()
        })
        builder.addCase(createAdminUsersThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // get all the admin users
        builder.addCase(getAdminUsersThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getAdminUsersThunk.fulfilled, (state, action) => {
            state.inProgress = false
            state.super_admin_user = action.payload.data.super_admins
            state.general_admin_user = action.payload.data.general_admins
        })
        builder.addCase(getAdminUsersThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // get selected admin user details
        builder.addCase(getAdminUserDetailThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getAdminUserDetailThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getAdminUserDetailThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // update the selected admin user details
        builder.addCase(updateAdminUserDetailThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(updateAdminUserDetailThunk.fulfilled, (state, action) => {
            state.inProgress = false
            sessionStorage.clear()
        })
        builder.addCase(updateAdminUserDetailThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // update the selected admin user details
        builder.addCase(getAllCandidatesThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getAllCandidatesThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getAllCandidatesThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // to get all the pending enquiries list
        builder.addCase(getAllPendingEnquiriesThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getAllPendingEnquiriesThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getAllPendingEnquiriesThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })
        
        // update the selected admin user details
        builder.addCase(getCandidateData.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getCandidateData.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getCandidateData.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // to get the active centre details
        builder.addCase(getEnquiryDetailsThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getEnquiryDetailsThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getEnquiryDetailsThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })
        
        // update the selected admin user details
        builder.addCase(postCandidateCommentsThunk.pending, (state, action) => {
            state.adminCommentsPostingProgress = true
            state.error = null
        })
        builder.addCase(postCandidateCommentsThunk.fulfilled, (state, action) => {
            state.adminCommentsPostingProgress = false
        })
        builder.addCase(postCandidateCommentsThunk.rejected, (state, action) => {
            state.adminCommentsPostingProgress = false
            state.error = action.payload
        })

        // to approve the enquiry
        builder.addCase(getEnquiryApproveThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getEnquiryApproveThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getEnquiryApproveThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(updateCentreDetailsThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(updateCentreDetailsThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(updateCentreDetailsThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })
        
        builder.addCase(DeleteCandidateCommentsThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(DeleteCandidateCommentsThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(DeleteCandidateCommentsThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // get all the centre admin/centre users
        builder.addCase(getCentresUsersThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getCentresUsersThunk.fulfilled, (state, action) => {
            state.inProgress = false
            state.centre_admins = action.payload.data.users.centre_admin.data
            state.centre_users = action.payload.data.users.centre_user.data
            state.allCenterList = action.payload.data.users
        })
        builder.addCase(getCentresUsersThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // get all active centres list
        builder.addCase(getAllActiveCentresListThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getAllActiveCentresListThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getAllActiveCentresListThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // get all active centres
        builder.addCase(getAllActiveCentresThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getAllActiveCentresThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getAllActiveCentresThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // to decline the enquiry
        builder.addCase(declineEnquiryThunk.pending, (state, action) => {
            state.declineInProgress = true
            state.error = null
        })
        builder.addCase(declineEnquiryThunk.fulfilled, (state, action) => {
            state.declineInProgress = false
        })
        builder.addCase(declineEnquiryThunk.rejected, (state, action) => {
            state.declineInProgress = false
            state.error = action.payload
        })

        // to make inactive the centre
        builder.addCase(centreInactiveThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(centreInactiveThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(centreInactiveThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // to get the centre details
        builder.addCase(getCentreDetailsThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getCentreDetailsThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getCentreDetailsThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })
        
        builder.addCase(getCandidateComments.pending, (state, action) => {
            state.listingInProgress = true
            state.error = null
        })
        builder.addCase(getCandidateComments.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(getCandidateComments.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(createNewCentreThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(createNewCentreThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(createNewCentreThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(createCentreUserThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(createCentreUserThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(createCentreUserThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(updateCentreUserThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(updateCentreUserThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(updateCentreUserThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(activateCandidateBySuperAdmin.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(activateCandidateBySuperAdmin.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(activateCandidateBySuperAdmin.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // upload image for centres
        builder.addCase(uploadsCentrePictureThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(uploadsCentrePictureThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(uploadsCentrePictureThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        // setup approved centres and invite centre admin
        builder.addCase(setUpApprovedCentresThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(setUpApprovedCentresThunk.fulfilled, (state, action) => {
            state.inProgress = false
        })
        builder.addCase(setUpApprovedCentresThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(otherRequestsThunk.pending, (state) => {
            state.makeInactiveInProgress = true
            state.error = null
        })
        builder.addCase(otherRequestsThunk.fulfilled, (state) => {
            state.makeInactiveInProgress = false
        })
        builder.addCase(otherRequestsThunk.rejected, (state, action) => {
            state.makeInactiveInProgress = false
            state.error = action.payload
        })

        builder.addCase(submitJobAdderIdThunk.pending, (state) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(submitJobAdderIdThunk.fulfilled, (state) => {
            state.inProgress = false
        })
        builder.addCase(submitJobAdderIdThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(reopenStageThunk.pending, (state) => {
            state.inProgress = true
            state.error = null
            state.reopenStageError = null
        })
        builder.addCase(reopenStageThunk.fulfilled, (state) => {
            state.inProgress = false
            state.reopenStageError = null
        })
        builder.addCase(reopenStageThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
            state.reopenStageError = (action.payload as any)?.message || 'Failed to reopen stage. Please try again.'
        })

        
        builder.addCase(getReferooDocumentThunk.pending, (state, action) => {
            state.inProgress = true
            state.error = null
        })
        builder.addCase(getReferooDocumentThunk.fulfilled, (state, action) => {
            state.inProgress = false
            const documents = action.payload.data.documents;
            state.referooRightToWorkDoc = documents.summary_report || [];
            state.referooReferenceCheckDoc = documents.questionnaire || documents.identity_verification || [];
            state.rightToWorkDoc = documents.right_to_work_check || [];
            state.policeCheckDoc = documents.police_check || [];
            state.referooDocumentsFound =
                state.referooRightToWorkDoc.length > 0 ||
                state.referooReferenceCheckDoc.length > 0 ||
                state.rightToWorkDoc.length > 0 ||
                state.policeCheckDoc.length > 0;
        })
        builder.addCase(getReferooDocumentThunk.rejected, (state, action) => {
            state.inProgress = false
            state.error = action.payload
        })

        builder.addCase(updateStepBypassReasonThunk.pending, (state) => {
            state.saveBypassInProgress = true
            state.error = null
        })
        builder.addCase(updateStepBypassReasonThunk.fulfilled, (state) => {
            state.saveBypassInProgress = false
        })
        builder.addCase(updateStepBypassReasonThunk.rejected, (state, action) => {
            state.saveBypassInProgress = false
            state.error = action.payload
        })
    }
});

export const {
    resetSuperAdminErrorAction,
    setAllCandidateListAction,
    setAllPendingEnquiriesAction,
    setEnquiryDetailsAction,
    setAllActiveCentresListAction,
    setAllActiveCentresAction,
    setCentreDetailsAction,
    setCandidateReviewDataAction,
    setCandidateAdminComments,
    setCentrePicAction,
    setAdminUserListAction,
    setTotalActiveCentresCountAction,
    setTotalPendingCentresCountAction,
    setTotalCentreAdminCountAction,
    setTotalCentreUserCountAction,
    clearAllPendingEnquiriesAction,
    resetCentrePicAction,
    setJobAdderIdPopupShowAction,
    resetReferooDocumentsAction
} = superAdminSlice.actions

export default superAdminSlice.reducer;